import { normalizeRing, type RingCall, ringKey } from './ringAnswer';

/**
 * The calls this module has heard about over the socket and not yet heard end.
 *
 * `call.ringing` names a call once — number, line, the contact it resolved to,
 * the conversation it threads into — and `call.answered` / `call.ended` name
 * only its key. So the ringing payload is kept for the life of the call, both
 * directions, and the answered event stamps it rather than replacing it: that
 * is how a call that is UP can still say whose it is and open its thread,
 * which the phone holding it cannot — a device knows a number and nothing
 * else.
 *
 * Pure: the provider (liveCalls.tsx) applies events and owns the timers.
 */
export interface LiveCallRecord extends RingCall {
  /** Epoch ms of `call.answered`, or null while still ringing. */
  answeredAt: number | null;
  /** Epoch ms the record was first heard — the stale sweep reads it. */
  heardAt: number;
}

export interface LiveCallsState {
  /** Inbound calls still ringing, oldest first — what raises a card. */
  rings: RingCall[];
  /** Every call heard ringing and not yet ended, both directions. */
  known: LiveCallRecord[];
}

export const NO_LIVE_CALLS: LiveCallsState = { rings: [], known: [] };

/** One `call.*` event applied. Unknown shapes leave the state untouched. */
export function applyCallEvent(
  state: LiveCallsState,
  name: string,
  payload: unknown,
  now: number,
): LiveCallsState {
  if (name === 'call.ringing') {
    const call = normalizeRing(payload);
    if (!call) return state;
    const existing = state.known.find((record) => record.key === call.key);
    const known = existing
      ? // Setup and Proceeding both ring; the later one may know more (the
        // contact resolved), the earlier one already holds the stamps.
        state.known.map((record) => (record.key === call.key ? { ...record, ...call } : record))
      : [...state.known, { ...call, answeredAt: null, heardAt: now }];
    const rings =
      call.direction === 'inbound' && !state.rings.some((ring) => ring.key === call.key)
        ? [...state.rings, call]
        : state.rings;
    return { rings, known };
  }
  const key = ringKey(payload);
  if (!key) return state;
  if (name === 'call.answered') {
    return {
      rings: state.rings.filter((ring) => ring.key !== key),
      known: state.known.map((record) =>
        record.key === key && record.answeredAt === null ? { ...record, answeredAt: now } : record,
      ),
    };
  }
  if (name === 'call.ended') {
    return {
      rings: state.rings.filter((ring) => ring.key !== key),
      known: state.known.filter((record) => record.key !== key),
    };
  }
  return state;
}

/** A ring the agent dismissed, or one that timed out: the card goes, the record stays. */
export function dropRing(state: LiveCallsState, key: string): LiveCallsState {
  if (!state.rings.some((ring) => ring.key === key)) return state;
  return { ...state, rings: state.rings.filter((ring) => ring.key !== key) };
}

/** Records older than `maxAgeMs` whose end was never heard — a missed webhook, a socket that reconnected late. */
export function sweepStale(state: LiveCallsState, now: number, maxAgeMs: number): LiveCallsState {
  const known = state.known.filter((record) => now - record.heardAt < maxAgeMs);
  return known.length === state.known.length ? state : { ...state, known };
}

/** The other party: who called, or who was called. */
export const peerOf = (call: Pick<RingCall, 'direction' | 'from' | 'to'>): string | null =>
  call.direction === 'outbound' ? call.to : (call.from ?? call.to);

const digits = (value: string | null | undefined): string => (value ?? '').replace(/\D/g, '');

/**
 * Whether two numbers are one number. `+1 212 555 0100`, `2125550100` and
 * `12125550100` all are; the national part is what the providers agree on,
 * so the comparison is on the last ten digits when both have that many.
 */
export function sameNumber(a: string | null | undefined, b: string | null | undefined): boolean {
  const left = digits(a);
  const right = digits(b);
  if (!left || !right) return false;
  if (left.length >= 10 && right.length >= 10) return left.slice(-10) === right.slice(-10);
  return left === right;
}

export interface LiveCallQuery {
  provider?: 'ringcentral' | 'twilio';
  /** The line the phone holding the call is registered on; a record on another line is another call. */
  inboxId?: number | null;
  /** The provider's own key for the call, when the phone holding it named one. */
  sessionKey?: string | null;
  /** The other party's number, for a phone that knows nothing else. */
  peer?: string | null;
}

/**
 * The record behind a call a phone here is holding. The key is exact; a
 * number is the fallback, because a Twilio device's leg SID is not the SID
 * the server keyed the call on, and RingCentral's widget does not always
 * name its session. An answered record wins over a ringing one — the one
 * the agent is on, not one still ringing at everyone.
 */
export function matchLiveCall(known: LiveCallRecord[], query: LiveCallQuery): LiveCallRecord | null {
  const pool = known.filter(
    (record) =>
      (!query.provider || record.provider === query.provider) &&
      (query.inboxId == null || record.inboxId === query.inboxId),
  );
  if (query.sessionKey) {
    const exact = pool.find((record) => record.key === query.sessionKey);
    if (exact) return exact;
  }
  if (!query.peer) return null;
  const byNumber = pool.filter((record) => sameNumber(peerOf(record), query.peer));
  // The NEWEST answered record: a call whose end was never heard stays known
  // for hours, and the first match by number was that stale one — the bar
  // then opened the thread of a call that ended this morning.
  const answered = byNumber.filter((record) => record.answeredAt !== null);
  return answered[answered.length - 1] ?? byNumber[byNumber.length - 1] ?? null;
}
